import { supabase } from '@/lib/supabase';
import { LIMIT_KEYS } from '@/types/licensing';

export type MigrationStatus = 'proposed' | 'applied' | 'reverted';

export interface MigrationSnapshotSubscription {
  id: string | null;
  system_code: string | null;
  plan_code: string | null;
  status: string | null;
  billing_cycle: string | null;
  price_agorot: number | null;
  effective_price_agorot: number | null;
  override_reason: string | null;
  expires_at: string | null;
}

export interface MigrationSnapshot {
  taken_at: string;
  subscription: MigrationSnapshotSubscription | null;
  all_subscriptions: MigrationSnapshotSubscription[];
  addons: Array<{ id: string; addon_code: string; quantity: number; status: string }>;
  usage: Record<string, number>;
}

export interface MigrationAddonInput {
  code: string;
  quantity: number;
}

export interface MigrationProposal {
  to_system_code: string;
  to_plan_code: string;
  addons: MigrationAddonInput[];
  effective_price_agorot: number | null;
  override_reason: string | null;
  notes: string | null;
}

export interface MigrationSuggestion {
  suggested_plan: 'ignite' | 'accelerate' | 'command';
  usage: Record<string, number>;
  addons: MigrationAddonInput[];
}

export interface MigrationLogRow {
  id: string;
  org_id: string;
  actor_id: string | null;
  actor_email: string | null;
  status: MigrationStatus;
  from_plan_code: string | null;
  from_system_code: string | null;
  to_plan_code: string;
  to_system_code: string;
  addons_applied: MigrationAddonInput[];
  effective_price_agorot: number | null;
  override_reason: string | null;
  snapshot: MigrationSnapshot;
  proposal: MigrationProposal;
  applied_at: string | null;
  reverted_at: string | null;
  notes: string | null;
  created_at: string;
}

async function buildSnapshot(orgId: string): Promise<MigrationSnapshot> {
  const [subsRes, addonsRes, usageRes] = await Promise.all([
    supabase
      .from('org_system_subscriptions')
      .select(
        'id, system_code, plan_code, status, billing_cycle, price_agorot, effective_price_agorot, override_reason, expires_at'
      )
      .eq('organization_id', orgId),
    supabase
      .from('org_addons')
      .select('id, addon_code, quantity, status')
      .eq('org_id', orgId)
      .eq('status', 'active'),
    supabase.rpc('org_usage', { p_org_id: orgId }),
  ]);

  if (subsRes.error) throw subsRes.error;
  if (addonsRes.error) throw addonsRes.error;
  if (usageRes.error) throw usageRes.error;

  const allSubs = (subsRes.data ?? []) as MigrationSnapshotSubscription[];
  // Capture whichever sub is currently live — active OR trial. Legacy orgs
  // on pro/trial need their prior row preserved in the snapshot for rollback.
  const activeSub =
    allSubs.find((s) => s.status === 'active' || s.status === 'trial') ?? null;

  // Normalize usage to the 13 canonical limit keys (org_usage only returns
  // keys it knows how to compute — missing keys default to 0).
  const usage: Record<string, number> = Object.fromEntries(LIMIT_KEYS.map((k) => [k, 0]));
  for (const row of (usageRes.data ?? []) as Array<{ limit_key: string; used: number }>) {
    usage[row.limit_key] = row.used;
  }

  return {
    taken_at: new Date().toISOString(),
    subscription: activeSub,
    all_subscriptions: allSubs,
    addons: (addonsRes.data ?? []) as MigrationSnapshot['addons'],
    usage,
  };
}

export const migrationApi = {
  buildSnapshot,

  async computeSuggestion(orgId: string): Promise<MigrationSuggestion> {
    const { data, error } = await supabase.rpc('migration_compute_suggestion', { p_org_id: orgId });
    if (error) throw error;
    return data as MigrationSuggestion;
  },

  async apply(params: {
    orgId: string;
    proposal: MigrationProposal;
    snapshot: MigrationSnapshot;
  }): Promise<string> {
    const { orgId, proposal, snapshot } = params;
    const { data, error } = await supabase.rpc('apply_migration', {
      p_org_id: orgId,
      p_to_system_code: proposal.to_system_code,
      p_to_plan_code: proposal.to_plan_code,
      p_addons: proposal.addons,
      p_effective_price_agorot: proposal.effective_price_agorot,
      p_override_reason: proposal.override_reason,
      p_snapshot: snapshot,
      p_proposal: proposal,
      p_notes: proposal.notes,
    });
    if (error) throw error;
    return data as string;
  },

  async revert(migrationId: string): Promise<void> {
    const { error } = await supabase.rpc('revert_migration', { p_migration_id: migrationId });
    if (error) throw error;
  },

  async listHistory(orgId: string): Promise<MigrationLogRow[]> {
    const { data, error } = await supabase
      .from('org_migration_log')
      .select('*')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as MigrationLogRow[];
  },

  async listCandidates(): Promise<
    Array<{
      org: { id: string; name: string; slug: string };
      subscription: { system_code: string; plan_code: string; status: string } | null;
      last_migration: MigrationLogRow | null;
    }>
  > {
    const { data: orgs, error: orgsErr } = await supabase
      .from('organizations')
      .select(
        'id, name, slug, org_system_subscriptions(system_code, plan_code, status)'
      )
      .is('archived_at', null)
      .order('name');
    if (orgsErr) throw orgsErr;

    const { data: logs, error: logsErr } = await supabase
      .from('org_migration_log')
      .select('*')
      .order('created_at', { ascending: false });
    if (logsErr) throw logsErr;

    const lastByOrg = new Map<string, MigrationLogRow>();
    for (const row of (logs ?? []) as MigrationLogRow[]) {
      if (!lastByOrg.has(row.org_id)) lastByOrg.set(row.org_id, row);
    }

    return ((orgs ?? []) as any[]).map((o) => {
      const subs = (o.org_system_subscriptions ?? []) as Array<{
        system_code: string;
        plan_code: string;
        status: string;
      }>;
      const active = subs.find((s) => s.status === 'active') ?? null;
      return {
        org: { id: o.id, name: o.name, slug: o.slug },
        subscription: active,
        last_migration: lastByOrg.get(o.id) ?? null,
      };
    });
  },
};
